import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, Eye, EyeOff, ArrowRight, ShieldCheck, BadgeCheck, QrCode } from "lucide-react";
import { api, errMsg, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1649769069590-268b0b994462?crop=entropy&cs=srgb&fm=jpg&q=70&w=900";

export default function AuthPage({ mode = "login" }) {
  const { setUser, settings } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = isLogin ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register", body);
      setUser(data);
      toast.success(isLogin ? "Selamat datang kembali!" : "Akun berhasil dibuat");
      nav(data.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [show, setShow] = useState(false);
  const year = new Date().getFullYear();
  const heroSrc = settings.has_login_hero ? `${API}/public/login-hero?v=${settings.version}` : HERO;

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-2">
      <aside className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 text-white bg-slate-950">
        <img src={heroSrc} alt="Worker UKerja" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 via-slate-950/70 to-slate-950" />
        <div className="relative"><Logo size="lg" light /></div>
        <div className="relative max-w-md">
          <h2 data-testid="login-title" className="font-display text-5xl font-extrabold leading-tight">{settings.login_title}</h2>
          <p data-testid="login-subtitle" className="mt-4 text-white/70">{settings.login_subtitle}</p>
          <Trust light />
        </div>
        <p className="relative text-xs text-white/50">Copyright © {year} {settings.app_name || "UKerja"}. All right reserved.</p>
      </aside>

      <div className="min-h-screen flex flex-col max-w-md w-full mx-auto lg:max-w-none lg:justify-center">
        <div className="lg:hidden relative overflow-hidden grain bg-gradient-to-br from-emerald-600 via-teal-700 to-slate-900 px-6 pt-12 pb-20 text-white">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/10" />
          <div className="absolute -left-10 bottom-0 w-32 h-32 rounded-full bg-emerald-300/20" />
          {settings.has_login_hero && <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
          <div className="relative"><Logo size="lg" light /><p data-testid="login-tagline" className="text-white/90 text-sm mt-3 max-w-xs">{settings.login_tagline}</p></div>
        </div>
        <div className="flex-1 -mt-10 lg:mt-0 bg-white rounded-t-[2rem] lg:rounded-none relative px-6 sm:px-10 pt-8 pb-6 flex flex-col lg:max-w-md lg:mx-auto lg:w-full lg:flex-none">
          <form onSubmit={submit} className="space-y-4 fade-up" data-testid={`${mode}-form`}>
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">{isLogin ? "Selamat datang" : "Buat akun baru"}</h1>
              <p className="text-sm text-slate-500 mt-1">{isLogin ? "Masuk untuk melanjutkan ke akun Anda." : "Daftar gratis dan mulai pesan jasa."}</p>
            </div>
            {!isLogin && (
              <IconField icon={User}><input data-testid="register-name-input" className="field h-12 pl-11" placeholder="Nama lengkap" value={form.name} onChange={set("name")} required /></IconField>
            )}
            <IconField icon={Mail}><input data-testid={`${mode}-email-input`} type="email" className="field h-12 pl-11" placeholder="nama@email.com" value={form.email} onChange={set("email")} required /></IconField>
            <IconField icon={Lock}>
              <input data-testid={`${mode}-password-input`} type={show ? "text" : "password"} className="field h-12 pl-11 pr-11" placeholder="Password (min. 6 karakter)" value={form.password} onChange={set("password")} required minLength={6} />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </IconField>
            {error && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
            <button data-testid={`${mode}-submit-button`} disabled={busy}
              className="w-full h-12 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/25 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {isLogin ? "Masuk" : "Daftar"} {!busy && <ArrowRight className="w-4 h-4" />}
            </button>
            <div className="flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-200" />atau<span className="flex-1 h-px bg-slate-200" /></div>
            <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"}
              className="w-full h-12 rounded-2xl border-2 border-slate-200 hover:border-emerald-300 text-slate-800 font-bold flex items-center justify-center transition-colors">
              {isLogin ? "Buat akun baru" : "Saya sudah punya akun"}
            </Link>
          </form>
          <div className="lg:hidden"><Trust /></div>
          <footer data-testid="auth-footer" className="mt-auto pt-8 text-center text-xs text-slate-400 lg:hidden">Copyright © {year} {settings.app_name || "UKerja"}. All right reserved.</footer>
        </div>
      </div>
    </div>
  );
}

function IconField({ icon: Icon, children }) {
  return <div className="relative"><Icon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />{children}</div>;
}

function Trust({ light }) {
  const items = [[ShieldCheck, "Escrow aman"], [BadgeCheck, "Worker terverifikasi"], [QrCode, "Bayar QRIS"]];
  return (
    <div className="mt-8 grid grid-cols-3 gap-2">
      {items.map(([I, l]) => (
        <div key={l} className={`rounded-2xl p-3 text-center ${light ? "bg-white/10 backdrop-blur" : "bg-slate-50"}`}>
          <I className={`w-5 h-5 mx-auto ${light ? "text-emerald-300" : "text-emerald-600"}`} />
          <p className={`text-[11px] font-bold mt-1 ${light ? "text-white/90" : "text-slate-700"}`}>{l}</p>
        </div>
      ))}
    </div>
  );
}
